import { SearchItem } from '../../../types/message';
import '../../../Styles/Layout/Items/SearchItemContainer.css';
import React, { useState, useRef, useEffect } from 'react';
import { CloseButton } from '../Buttons/CloseButton';
import { createPortal } from 'react-dom';

export const SearchItemContainer = ({ searchItems }: { searchItems: SearchItem[] }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isExpanded) {
        setIsExpanded(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isExpanded]);

  useEffect(() => {
    if (isExpanded) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isExpanded]);

  const handleSearchItemClick = (item: SearchItem, e: React.MouseEvent) => {
    e.stopPropagation();
    if (item.url) {
      window.open(item.url, '_blank', 'noopener,noreferrer');
    }
  };

  const toggleExpanded = (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    setIsExpanded(!isExpanded);
  };

  const getFaviconUrl = (url: string) => {
    try {
      const domain = new URL(url).hostname;
      return `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
    } catch (e) {
      return null;
    }
  };

  const handleModalClick = (e: React.MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      setIsExpanded(false);
    }
  };

  if (!searchItems || searchItems.length === 0) return null;

  // Limit the number of favicons shown in the trigger, deduped by host
  const maxTriggerFavicons = 4;
  const uniqueSearchItemsByHost: SearchItem[] = [];
  const seenHosts = new Set<string>();
  for (const item of searchItems) {
    if (!item.url) continue;
    let host: string;
    try {
      host = new URL(item.url).hostname;
    } catch (e) {
      continue;
    }
    if (!seenHosts.has(host)) {
      seenHosts.add(host);
      uniqueSearchItemsByHost.push(item);
    }
  }
  const triggerFavicons = uniqueSearchItemsByHost.slice(0, maxTriggerFavicons);
  const remainingCount = uniqueSearchItemsByHost.length - maxTriggerFavicons;



  return (
    <>
    <div className={`search-item-container`}>
      <div className="source-favicon-trigger" onClick={toggleExpanded}>
        {triggerFavicons.map((item: SearchItem, index: number) => {
          const faviconUrl = item.url ? getFaviconUrl(item.url) : null;
          const displayName = item.title ? item.title.charAt(0).toUpperCase() : 'S';
          return (
            <div key={index} className="trigger-favicon-item" title={item.title || 'Source'}>
              {faviconUrl ? (
                <img
                  src={faviconUrl}
                  alt=""
                  className="trigger-favicon-img"
                  onError={e => {
                    (e.target as HTMLImageElement).style.display = 'none';
                    const parent = (e.target as HTMLImageElement).parentNode as HTMLElement;
                    if (parent) {
                      parent.innerHTML = `<div class="trigger-favicon-placeholder">${displayName}</div>`;
                    }
                  }}
                />
              ) : (
                <div className="trigger-favicon-placeholder">
                  {displayName}
                </div>
              )}
            </div>
          );
        })}
        {remainingCount > 0 && (
          <div className="trigger-favicon-more">Sources</div>
        )}
      </div>
    </div>

      {isExpanded && createPortal(
        <div className="source-modal-overlay" onClick={handleModalClick}>
          <div className="source-modal" ref={modalRef}>
            <div className="source-modal-header">
              <span className="source-modal-title">Sources</span>
              <CloseButton onClick={() => toggleExpanded()} />
            </div>
            <div className="source-modal-content">
              {searchItems.map((item: SearchItem, index: number) => {
                const faviconUrl = item.url ? getFaviconUrl(item.url) : null;
                const displayUrl = item.url ? new URL(item.url).hostname.replace('www.', '') : '';
                const displayName = item.title ? item.title.charAt(0).toUpperCase() : 'S';
                return (
                  <div
                    key={index}
                    onClick={(e) => handleSearchItemClick(item, e)}
                    className="source-item"
                    style={{ '--animation-order': index } as React.CSSProperties}
                  >
                      <div className="source-item-details">
                        <div className="source-item-title" title={item.title || 'Untitled Document'}>
                          {item.title || 'Untitled Document'}
                        </div>
                      </div>
                    <div className="source-item-icon-container">
                      {faviconUrl ? (
                        <img
                          src={faviconUrl}
                          alt=""
                          className="source-item-favicon"
                          onError={e => {
                            (e.target as HTMLImageElement).style.display = 'none';
                            const parent = (e.target as HTMLImageElement).parentNode as HTMLElement;
                            if (parent) {
                              parent.innerHTML = `<div class="source-item-icon-placeholder">${displayName}</div>`;
                            }
                          }}
                        />
                      ) : (
                        <div className="source-item-icon-placeholder">
                          {displayName}
                        </div>
                      )}
                                          {displayUrl && <div className="source-item-url" title={item.url}>{displayUrl}</div>}
                    </div>

                  </div>
                );
              })}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};